/**
 * Read-only JWT helpers. They DECODE the payload and never verify the signature — the server is the
 * only authority on whether a token is valid. Use them as a hint ("this token's exp has passed, ask the
 * server before trusting it"), never as an access decision on their own.
 */

const decodeBase64UrlToUtf8 = (segment: string): string => {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);

  // atob yields one char per byte; percent-encode the bytes so decodeURIComponent reassembles UTF-8.
  return decodeURIComponent(Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""));
};

const decodeJwtPayload = (token: string | null | undefined): Record<string, unknown> | null => {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3 || !parts[1]) return null;

  try {
    const payload = JSON.parse(decodeBase64UrlToUtf8(parts[1]));

    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
};

/** The token's `exp` claim in epoch milliseconds, or null when the token is missing, malformed or has no numeric `exp`. */
export const getJwtExpiryMs = (token: string | null | undefined): number | null => {
  const exp = decodeJwtPayload(token)?.exp;

  return typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : null;
};

/** The token's `sub` claim (the account email on the legacy API), or null when it is missing or not a string. */
export const getJwtSubject = (token: string | null | undefined): string | null => {
  const sub = decodeJwtPayload(token)?.sub;

  return typeof sub === "string" && sub ? sub : null;
};

/**
 * True only when the token carries an `exp` that is already in the past by the LOCAL clock. A token
 * whose expiry cannot be read is reported as not expired, so an unexpected token shape never turns
 * into a false "expired" — the server still decides.
 */
export const isJwtExpired = (token: string | null | undefined, nowMs: number = Date.now()): boolean => {
  const expMs = getJwtExpiryMs(token);

  return expMs !== null && expMs <= nowMs;
};
